import { LitElement, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { ApiFactory } from '../../api/agwasuite-api-factory';
import { ApiUserMaintenance } from '../../api/core/api-user-maintenance';
import { SecurityApi } from '../../api/security/security-api';
import {
  GroupDomainMapping,
  GroupSiteSummary,
  LoginGroup,
  LoginGroupImis,
  MembershipStatus,
  SecurityDomainDto,
} from '../../api/agwa-api-model';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import { renderOperationTypeChips } from './shared/operation-type-chips';
import '../../components/search/agwa-search';
import '../../components/empty-state/agwa-empty-state';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import { styles } from './view-groups-styles';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';

import { WellKnownIds } from '../../utility/claim-id';

import './member-legend.js';
// The table, the member's own page and the legend read their labels from one place, so they cannot
// drift apart.
import {
  MEMBERSHIP_ORDER,
  membershipLabel,
  operatingStatusLabel,
  toMembershipStatus,
} from './shared/company-status-display';
import '../../components/state-badge/agwa-state-badge';
import {
  FACET_MEMBERSHIP,
  FACET_OPERATING,
  FACET_TYPES,
  OPERATING_ACTIVE,
  OPERATING_BLOCKED,
} from './shared/company-list-facets';
import {
  collapseOperationTypeCodes,
  getOrganizationTypeLabel,
} from './shared/organization-type-labels';
// The repository's standing comparison for enum-like strings arriving from an API response.
// Domain codes are exactly that, and hand-rolling the comparison here is what produced the
// vocabulary mismatch this replaces.
import { isSame } from '../../utility/strings/strings-utils';
import '../../components/filter/agwa-filter-rail';
import '../../components/filter/agwa-filter-summary';
import '../../components/layout/agwa-fullscreen-block';
import { SEARCH_CLAUSE_KEY } from '../../components/filter/agwa-filter-summary';
import type {
  FilterFacet,
  FilterSelection,
} from '../../components/filter/filter-facets';
import {
  isSelectionEmpty,
  selectedValues,
} from '../../components/filter/filter-facets';
import { terms } from '../../language/terms';
import { preservedCasing } from '../../language/preserved-casing';
import { resolveRouterPath, router } from '../../router';

import '../../components/data-table/data-table';
import type {
  DataTableColumnDef,
  DataTable,
} from '../../components/data-table/data-table';
import type {
  DataTableDataProvider,
  DataTableDataRequest,
  DataTableDataResponse,
} from '../../components/data-table/data-table-provider';

/** Extended row type that includes computed imisReference and domains for the table */
/**
 * One row of the company list, whether it is a company or one of its sites.
 *
 * ONE shape for both, deliberately. The table renders child rows through the SAME column
 * definitions as their parents, so a column reads the same field name off either - and the only way
 * a column can be blank on one and filled on the other is if the DATA says so. That is why a company
 * row carries `siteName: undefined` rather than the columns branching on row kind: the branch would
 * be a second place for the two to disagree, which is exactly the drift that put "Inactive" beside
 * "Blocked" on this screen when the sites lived in a nested table of their own.
 */
interface GroupRow {
  /** Company id on a company row, site id on a site row. Unique across both, so it can key rows. */
  id: string;
  rowKind: 'company' | 'site';
  /** Always the owning company, so a site row can be acted on without walking back up. */
  companyId: string;
  /** Repeated on every site row, which is what keeps a site legible once its company has scrolled off. */
  companyName: string;
  /** Absent on a company row. Its emptiness is what leaves the Site column blank. */
  siteName?: string;
  imisReference: string;
  active: boolean;
  /**
   * The COMPANY's membership standing, repeated onto its site rows.
   *
   * A site has no standing of its own, but it is covered by its company's - and a blank cell beside
   * a company chipped "Member" reads as a denial rather than as an absence, which is the falser of
   * the two. Architect's ruling, 2026-08-10 (`@CO-CHILD-004`).
   */
  membershipStatus?: string;
  /** Union of own and site types on a company row; the site's own types on a site row. */
  domainCodes: string[];
  /**
   * The types the company holds ITSELF, with its sites' contributions excluded.
   *
   * Carried separately from `domainCodes` because narrowing the child rows turns on exactly this
   * distinction: a company on the list because of its own group-level type keeps all of its sites,
   * while one carried onto the list by a site shows only the site that carried it. The union alone
   * cannot tell those two cases apart. Empty on a site row, which has no sites of its own.
   */
  ownDomainCodes: string[];
}

@customElement('view-groups')
export class ViewGroups extends WithUserProfileConsumer(LitElement) {
  @state() private groups: LoginGroup[] = [];
  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private search = '';
  /**
   * Which values are ticked in the filter rail, keyed by facet.
   *
   * One object rather than a field per filter, because the rail and the query sentence both take
   * the whole selection: splitting it would mean assembling it again at every render and leave two
   * places that can disagree about what is currently applied.
   *
   * A facet with no ticked values is not narrowing anything. That is what removed the "All" option
   * the old operating-state radio group needed - with independent checkboxes the empty set already
   * says "all", and an explicit All is a fourth control that can contradict the other three.
   *
   * Holds canonical keys, never the words on screen. Labels are a display concern that changes;
   * the key is what the API sends.
   */
  @state() private filterSelection: FilterSelection = {};

  /**
   * The three views of the selection the filter predicates below read.
   *
   * Exposed as getters so the predicates keep the shape they were written and reviewed in - the
   * change here is where the values come from, not what they mean.
   */
  private get operatingFilter(): string[] {
    return selectedValues(this.filterSelection, FACET_OPERATING);
  }
  private get membershipFilter(): MembershipStatus[] {
    return selectedValues(
      this.filterSelection,
      FACET_MEMBERSHIP
    ) as MembershipStatus[];
  }
  private get selectedDomains(): string[] {
    return selectedValues(this.filterSelection, FACET_TYPES);
  }
  @state() private imis: LoginGroupImis[] = [];

  /** Available security domains for filtering (both Group and Site level) */
  @state() private availableDomains: SecurityDomainDto[] = [];

  /**
   * The full domain catalogue, unfiltered, for the code -> short-code map the
   * operation-type chips read. Short codes are SERVER data (SecurityDomainDto
   * .shortCode), never UI literals - AGWA owns the abbreviations and corrects them
   * without a deployment, the same reasoning D-086 records for the register label.
   */
  @state() private domainCatalogue: SecurityDomainDto[] = [];

  /** Map of groupId -> domain mapping (includes group and site domains) */
  @state() private groupDomainMap: Map<string, GroupDomainMapping> = new Map();

  /** Map of groupId -> site summaries */
  @state() private groupSitesMap: Map<string, GroupSiteSummary[]> = new Map();

  /** Reference to the data table for triggering refreshes */
  @query('agwa-lit-table') private dataTable?: DataTable;

  static styles = [styles];

  /**
   * Column definitions, serving BOTH company rows and their site child rows.
   *
   * There is one set, not two, and that is the point. The table renders a child through the same
   * columns as its parent, so the two cannot fall out of alignment and cannot describe the same fact
   * in different words. The previous nested sites table had its own columns and its own labels, and
   * it drifted: it said a site was "Inactive" while the company row directly above said "Blocked".
   * Both now read `operatingStatusLabel`, so there is nowhere for that to happen again.
   */
  private get groupColumns(): DataTableColumnDef[] {
    return [
      {
        // Wrapped because the header row uppercases its content (`data-table.styles.css:115`),
        // which turned this into "IMIS REFERENCE" on screen - the one spelling this repository has
        // a standing rule against. The term itself was always correct; the transform rewrote it
        // afterwards, where no compiler or term test can see.
        title: preservedCasing(`${terms.imis.value} Reference`),
        fieldName: 'imisReference',
        width: '140px',
        fixed: 'left',
      },
      {
        // Company and site are SEPARATE columns, and a site row repeats its company's name rather
        // than leaving it blank. That repetition is what makes a site row still legible once its
        // company's own row has scrolled out of view, which is the failure the previous panel had.
        title: `${terms.loginGroup.value} Name`,
        fieldName: 'companyName',
        width: '260px',
        sortable: true,
        // Text-rendering displayValue, so it opts into the ellipsis + hover-expand that
        // plain columns get by default (the overflow affordance, architect 2026-09-05).
        clip: true,
        displayValue: (_table: DataTable, item: GroupRow) =>
          // Muted on a site row, because the name is repeated there for legibility rather than
          // being what the row IS: the site's own name is the subject, and two names at equal
          // weight make a reader work out which one the row is about. A company row keeps the
          // inherited weight, so the contrast is what distinguishes the two kinds.
          //
          // WHY THE COLOUR IS SET HERE RATHER THAN IN THIS PAGE'S STYLESHEET. `agwa-lit-table`
          // renders its rows into its own SHADOW ROOT, so a class defined in
          // view-groups-styles.styles.css cannot reach a cell - which is also why
          // `@CO-TYPES-005`'s sweep had to read through a locator rather than off the host. A
          // custom property DOES cross that boundary, because custom properties inherit, so
          // naming the role token here keeps the colour decision in tokens.css where every other
          // one lives.
          item.rowKind === 'site'
            ? html`<span style="color: var(--agwa-text-muted)"
                >${item.companyName}</span
              >`
            : html`${item.companyName}`,
      },
      {
        title: `${terms.loginSite.value} Name`,
        fieldName: 'siteName',
        width: '240px',
      },
      {
        // Replaces the old Description column; a company no longer has one (D-138). On a
        // company this is the union of its own types and its sites'; on a site it is that
        // site's own.
        // SHORT CODES, sized to the header (architect, 2026-09-03 screenshot note:
        // "operating types can be not much bigger than the header" - the full names
        // made the column the widest thing on the list). The expansion lives in one
        // hoisted tooltip over the strip, so the names are one hover away.
        title: terms.userGroupDomain.plural,
        fieldName: 'domainCodes',
        width: '150px',
        displayValue: (_table: DataTable, item: GroupRow) => {
          // Blank when a row holds none - architect's ruling, 2026-08-10, `@CO-TYPES-006`. The
          // earlier "No operation types" text was defending against a blank cell being read as data
          // that failed to arrive, but it printed a negative on every row that holds none, and on a
          // list where holding none is ordinary that reads as noise rather than as information. An
          // empty set renders as an empty cell.
          // One badge where a broader type already implies a narrower one: whatever both supplies
          // and manufactures its system is described by the manufacturer alone. Architect's ruling,
          // 2026-08-30, widened 2026-09-17 to every row kind - a site row collapsed differently
          // from its company row until then.
          //
          // Applied HERE rather than to `domainCodes` itself, because the type filter reads that
          // same field: collapsing the data would stop a filter on Prime System Supplier matching a
          // row that genuinely is one.
          const codes = collapseOperationTypeCodes(item.domainCodes);

          if (codes.length === 0) {
            return html``;
          }
          // The legend of every code opens over the chips in the top layer, so no row or the
          // sticky header paints over it (operation-type-chips.ts, U33 item 7).
          return renderOperationTypeChips(
            codes.map((code) => ({
              code: this.shortCodeFor(code),
              label: getOrganizationTypeLabel(code),
            }))
          );
        },
      },
      {
        title: 'Active',
        fieldName: 'active',
        width: '90px',
        sortable: true,
        displayValue: (_table: DataTable, item: GroupRow) =>
          html`<agwa-state-badge
            domain="companyOperating"
            state=${operatingStatusLabel(item.active)}
          ></agwa-state-badge>`,
      },
      {
        // Shown next to Status deliberately, because the two used to be one flag and the whole
        // point of the split is that they are read separately: a company can be a member and
        // blocked, or a non-member working away perfectly well.
        //
        // A site row REPEATS its company's membership rather than leaving the cell blank.
        // Architect's ruling, 2026-08-10, reversing the first implementation of this column. The
        // earlier reasoning - that membership is a standing only a company holds, so a site row
        // should say nothing - was true about the data model and wrong about the screen: a blank
        // cell beside a company chipped "Member" reads as "this site is NOT a member", which is a
        // stronger and falser claim than the repetition it was avoiding. A site of a member company
        // IS covered by that membership, so the row says so.
        //
        // It repeats the SAME value from the SAME row data, so the two cannot disagree - which is
        // the identical argument that puts the company's name on every site row.
        title: terms.membership.value,
        fieldName: 'membershipStatus',
        width: '120px',
        sortable: true,
        displayValue: (_table: DataTable, item: GroupRow) => {
          if (item.membershipStatus === undefined) {
            return html``;
          }
          return html`<agwa-state-badge
            domain="membership"
            state=${item.membershipStatus}
          ></agwa-state-badge>`;
        },
      },
      // The Actions column is GONE (architect, 2026-09-03): its one surviving entry
      // was Staff, and the company workspace the row itself opens carries a Staff tab,
      // so the column spent 90 fixed pixels duplicating a destination one click away.
      // The freed width went to the two name columns - "site and company important".
    ];
  }

  /**
   * The site rows belonging to a company, as rows of this same table.
   *
   * Returns the sites of a COMPANY row only. A site row has nothing beneath it, and returning
   * anything for one would render a second level the data does not have.
   *
   * The site's own operating state goes through `operatingStatusLabel` at render time via the
   * shared Active column, so a site and its company are described in the same words by
   * construction - see the column definitions above for why that matters here specifically.
   */
  /**
   * Open whatever the clicked row IS - the company for a company row, the site for a site row.
   *
   * A site row's `id` is the SITE's id and its company is in `companyId`, so the two kinds cannot
   * share one URL shape. Routing every row to `maintenance/companies/{id}` built a company URL out of
   * a site id, which is why clicking a site row went nowhere useful.
   */
  private openRow(row: GroupRow): void {
    const path =
      row.rowKind === 'site'
        ? `maintenance/companies/${row.companyId}/sites/${row.id}`
        : `maintenance/companies/${row.id}`;
    // `void` because the router's navigation promise has nothing to report back here: the row has no
    // state to roll back if it rejects, and the route change is the whole outcome.
    void router?.navigate(resolveRouterPath(path));
  }

  private siteRowsFor = (item: object): GroupRow[] => {
    const row = item as GroupRow;
    if (row.rowKind !== 'company') return [];
    const sites = this.groupSitesMap.get(row.id.toLowerCase()) || [];
    return sites
      .filter((site) => this.siteSurvivesNarrowing(row, site))
      .map((site) => ({
        id: site.id,
        rowKind: 'site' as const,
        companyId: row.id,
        companyName: row.companyName,
        siteName: site.name ?? '',
        imisReference: '',
        active: site.active ?? false,
        // Inherited from the company, because that is what is TRUE of the site: a site of a member
        // company is covered by that membership. The site's own operating state is NOT inherited -
        // it sits in the Active column and is genuinely the site's own, which is the distinction
        // this row is making. Inherit what the company's standing says about the site; keep what
        // the site decides for itself.
        membershipStatus: row.membershipStatus,
        domainCodes: site.domainCodes ?? [],
        // A site has no sites, so there is no union for this to be the "own" half of.
        ownDomainCodes: site.domainCodes ?? [],
      }));
  };

  /**
   * Whether a site is listed beneath its company under the narrowing currently applied.
   *
   * Architect's ruling, 2026-08-10, recorded on `@CO-TYPEFILTER-002`: when a company is on the list
   * ONLY because one of its sites matched, show the parent row and then the MATCHING child rows.
   * The question a narrowed list is answering is "which site", and listing the company's other
   * sites answers it with noise - the row that carried the company here is not distinguishable from
   * the rows that came along with it.
   *
   * The rule is scoped to "carried here by a site" rather than applied to every narrowing, and that
   * scope is load-bearing. When a company matched in its own right, nothing was being asked about
   * its sites, so hiding any of them would state something false about which sites it has - and it
   * would make searching a company by name empty out its own site list, which is the ordinary case.
   *
   * Search text and operation type are the only two criteria a SITE can carry a company through.
   * Operating state and membership are read off the company alone and never reach here.
   */
  private siteSurvivesNarrowing(
    company: GroupRow,
    site: GroupSiteSummary
  ): boolean {
    const searchText = this.search.trim().toLowerCase();
    if (searchText && !this.companyMatchesTextItself(company, searchText)) {
      if (!site.name?.toLowerCase().includes(searchText)) return false;
    }

    // A plain code comparison, which is only honest now that BOTH sides are codes. It used to
    // compare a site's `types` - display names off `JobGetGroups` - against a selection holding
    // codes, so it matched nothing and hid every child row whenever a type filter was applied. The
    // fix went into the DTO rather than into a normalising helper here: the wire carries the code
    // and the client resolves the word, so there is one vocabulary and nothing left to bridge.
    if (
      this.selectedDomains.length > 0 &&
      !company.ownDomainCodes.some((code) =>
        isSame(code, ...this.selectedDomains)
      )
    ) {
      if (
        !(site.domainCodes ?? []).some((code) =>
          isSame(code, ...this.selectedDomains)
        )
      )
        return false;
    }

    return true;
  }

  /**
   * Whether the company's own fields contain the search text, ignoring its sites. Its name is the
   * only text field it has: the description the search also matched is gone (D-138).
   */
  private companyMatchesTextItself(company: GroupRow, text: string): boolean {
    return company.companyName.toLowerCase().includes(text);
  }

  connectedCallback() {
    super.connectedCallback();
    void this.fetchData();
  }

  /**
   * The short code for a domain code, from the catalogue, lower-cased lookup per the
   * standing rule on API strings. Falls back to the full label: a missing short code
   * is ordinary (the catalogue marks them optional), and a chip with the long word is
   * information while an empty chip is a rendering bug.
   */
  private shortCodeFor(code: string): string {
    const hit = this.domainCatalogue.find(
      (d) => d.code?.toLowerCase() === code.toLowerCase()
    );
    return hit?.shortCode || getOrganizationTypeLabel(code);
  }

  async fetchData() {
    this.loading = true;
    this.error = null;
    this.groups = [];
    try {
      // Fetch groups (with domain mappings and sites) and domain definitions in parallel
      const userApi = ApiFactory.authenticated(ApiUserMaintenance);
      const securityApi = ApiFactory.authenticated(SecurityApi);

      const [groupsResp, domainsResp] = await Promise.all([
        userApi.getGroups(),
        securityApi.getSecurityDomains(),
      ]);

      if (groupsResp.success && groupsResp.data?.groups) {
        // Exclude the AGWA system tenant group from the member list
        this.groups = groupsResp.data.groups.filter(
          (x) => x.id.toLowerCase() !== WellKnownIds.agwaGroupId
        );
        this.imis = groupsResp.data.imis || [];

        // Build domain map from the response (includes both group and site domains)
        const domainMap = new Map<string, GroupDomainMapping>();
        if (groupsResp.data.domainMappings) {
          for (const [groupId, mapping] of Object.entries(
            groupsResp.data.domainMappings
          )) {
            domainMap.set(groupId.toLowerCase(), mapping);
          }
        }
        this.groupDomainMap = domainMap;

        // Build sites map from the response
        const sitesMap = new Map<string, GroupSiteSummary[]>();
        if (groupsResp.data.groupSites) {
          for (const [groupId, sites] of Object.entries(
            groupsResp.data.groupSites
          )) {
            sitesMap.set(groupId.toLowerCase(), sites);
          }
        }
        this.groupSitesMap = sitesMap;
      } else {
        this.error =
          groupsResp.error || `Failed to load ${terms.loginGroup.lowerPlural}`;
        return;
      }

      if (domainsResp.success && domainsResp.data?.domains) {
        // Offer only the types AGWA actually assigns to companies. `uiAssignable` comes from
        // security-catalog.yml and reaches the client on the DTO, so a type becoming assignable
        // later appears here with no code change - which the previous hard-coded exclusion of
        // AGWA.SystemTenant could never do. It also excluded exactly one type by name, so every
        // other non-assignable type was being offered as a filter that matches nothing.
        this.availableDomains = domainsResp.data.domains.filter(
          (d) => d.uiAssignable
        );
        this.domainCatalogue = domainsResp.data.domains;
      }
    } catch (e: unknown) {
      this.error =
        (e as Error)?.message ||
        `Error loading ${terms.loginGroup.lowerPlural}`;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Data provider for the table. Reads current filter state from component properties.
   * This is bound once to the table - when filters change, we call table.fetchData() to refresh.
   */
  private groupsProvider: DataTableDataProvider<GroupRow> = async (
    request: DataTableDataRequest
  ): Promise<DataTableDataResponse<GroupRow>> => {
    let rows: GroupRow[] = this.groups.map((g) => {
      const mapping = this.groupDomainMap.get(g.id.toLowerCase());
      return {
        id: g.id,
        rowKind: 'company' as const,
        companyId: g.id!,
        companyName: g.name ?? '',
        // Left undefined on purpose: the Site column reads this field for both kinds of row, so a
        // company's blank cell comes from the data rather than from the column branching.
        siteName: undefined,
        imisReference: this.getImisReference(g),
        active: g.active ?? false,
        membershipStatus: g.membershipStatus,
        domainCodes: mapping?.allDomains || [],
        ownDomainCodes: mapping?.groupDomains || [],
      };
    });

    // Free text reaches SITE names as well as the company's own name. Without that,
    // searching for a site returns nothing at all, and the sites are already held on this page - so
    // the search box was quietly narrower than the data behind it.
    const searchText = this.search.trim().toLowerCase();
    if (searchText) {
      rows = rows.filter(
        (row) =>
          row.companyName.toLowerCase().includes(searchText) ||
          this.sitesMatchingText(row.id, searchText).length > 0
      );
    }

    // Apply operating-state filter - show companies in ANY of the ticked states.
    //
    // Nothing ticked and both ticked are the same question, and the predicate must not
    // distinguish them: "Active or Blocked" is every company, which is what an empty facet
    // already means. The old radio group needed a third "All" option to say this; independent
    // checkboxes say it by having none ticked.
    if (this.operatingFilter.length > 0) {
      rows = rows.filter((g) =>
        this.operatingFilter.includes(
          g.active ? OPERATING_ACTIVE : OPERATING_BLOCKED
        )
      );
    }

    // Apply membership filter - show companies in ANY of the selected states.
    //
    // The row's value is normalised before it is compared, because the wire format is camelCase
    // while the generated model declares PascalCase - a known contract defect recorded in the DoD
    // record for #324. Comparing the raw string would silently match nothing.
    if (this.membershipFilter.length > 0) {
      // Only company rows are filtered here - site rows are produced later, by the child-row
      // provider, and are never candidates. A row carrying no membership standing therefore cannot
      // satisfy a membership filter, and excluding it is the honest answer rather than a fallback
      // to some default state.
      rows = rows.filter(
        (g) =>
          g.membershipStatus !== undefined &&
          this.membershipFilter.includes(toMembershipStatus(g.membershipStatus))
      );
    }

    // Apply domain filter - show groups that have ANY of the selected domains
    if (this.selectedDomains.length > 0) {
      rows = rows.filter((g) =>
        g.domainCodes.some((dc) => this.selectedDomains.includes(dc))
      );
    }

    // SHOW WHAT MATCHED. A company can be returned purely because one of its SITES matched - the
    // type filter has always worked on the union of company and site types, and search now reaches
    // site names too - and until now the reason was invisible: the row appeared with nothing on it
    // explaining why, and finding out meant opening rows one at a time. That is not a search result.
    //
    // Which of its sites are shown is decided by `siteSurvivesNarrowing`, not here.
    this.applyAutoExpansion(rows);

    // Apply sorting if requested
    const [sortDescriptor] = request.sort;
    if (sortDescriptor) {
      const direction = sortDescriptor.direction === 'desc' ? -1 : 1;
      rows = [...rows].sort((a, b) => {
        const aVal = a[sortDescriptor.field as keyof GroupRow];
        const bVal = b[sortDescriptor.field as keyof GroupRow];
        if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
          return (aVal === bVal ? 0 : aVal ? -1 : 1) * direction;
        }
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return aVal.localeCompare(bVal) * direction;
        }
        return 0;
      });
    }

    // Slice for pagination (though we show all)
    const start = request.sliceStart ?? 0;
    const end = start + request.sliceLength + 1;
    const window = rows.slice(start, end);
    const items = window.slice(0, request.sliceLength);

    return {
      items,
      totalCount: rows.length,
      hasMore: window.length > request.sliceLength,
    };
  };

  /**
   * A box was ticked or unticked in the rail. The rail hands back the whole selection rather than
   * one change, so there is nothing to merge here and no chance of the page's copy drifting from
   * the control's.
   */
  private handleFilterChange(e: CustomEvent<{ selection: FilterSelection }>) {
    this.filterSelection = e.detail.selection;
    this.refreshTable();
  }

  /**
   * Everything narrowing the list is undone at once - the ticks AND the search box.
   *
   * Clearing only the facets would be the more literal reading of a control that sits in the
   * filter rail, and it would be wrong: someone who has searched and filtered and wants the full
   * list back has one thing in mind, and leaving the search applied hands them a list that is
   * still narrowed by something they just asked to clear.
   */
  private handleFilterClear() {
    this.filterSelection = {};
    this.search = '';
    this.refreshTable();
  }

  /** One condition was dropped from the query sentence, leaving the rest of the query in place. */
  private handleFilterDrop(e: CustomEvent<{ key: string }>) {
    if (e.detail.key === SEARCH_CLAUSE_KEY) {
      this.search = '';
    } else {
      this.filterSelection = { ...this.filterSelection, [e.detail.key]: [] };
    }
    this.refreshTable();
  }

  /**
   * Narrows the list to the search the box reports, once. The box reports one search twice -
   * `search-input` as the person types and `search-change` when they press Enter or clear - so a
   * value already searched is not searched again, which reloaded the table a second time
   * (#398 C15 d). A clear is reported as `search-clear` and then a change to nothing, so the change
   * finds the box already empty and is the one dropped.
   */
  private handleSearchInput(e: CustomEvent<{ value: string }>) {
    if (e.detail.value === this.search) return;
    this.search = e.detail.value;
    this.refreshTable();
  }

  private handleSearchClear() {
    this.search = '';
    this.refreshTable();
  }

  /**
   * Refreshes the table data. The provider reads current filter state from component properties.
   */
  private refreshTable() {
    // Ask the table to re-fetch data using the existing provider
    // The provider will read current filter state when called
    void this.dataTable?.fetchData();
  }

  /**
   * The companies surviving a given query, over the page's own copy of the data.
   *
   * Parameterised on the selection rather than reading `this.filterSelection` directly, because
   * the facet counts in the rail need this same predicate run with ONE facet ignored. Counted any
   * other way, ticking "Member" renders every sibling in that facet as `0` - no company is in two
   * membership states at once - and a count that reads zero beside a value you can still usefully
   * tick looks like a broken screen.
   *
   * Every clause here must match the data provider's exactly. When the two disagree the screen
   * shows the empty state over a table that has rows in it, or hides an empty state it needs.
   */
  private filterGroups(
    selection: FilterSelection,
    searchText: string
  ): LoginGroup[] {
    const text = searchText.trim().toLowerCase();
    const operating = selectedValues(selection, FACET_OPERATING);
    const membership = selectedValues(selection, FACET_MEMBERSHIP);
    const domains = selectedValues(selection, FACET_TYPES);

    return this.groups.filter((g) => {
      // Sites included, matching the provider: a company can be carried into the list purely
      // because one of its sites matched.
      if (
        text &&
        !g.name?.toLowerCase().includes(text) &&
        this.sitesMatchingText(g.id!, text).length === 0
      ) {
        return false;
      }
      if (
        operating.length > 0 &&
        !operating.includes(g.active ? OPERATING_ACTIVE : OPERATING_BLOCKED)
      ) {
        return false;
      }
      // A company carrying no membership standing cannot satisfy a membership filter. Excluding it
      // is the honest answer rather than falling back to some default state, and it matches the
      // provider - which this getter previously did NOT, having omitted the undefined check.
      if (membership.length > 0) {
        if (g.membershipStatus === undefined) return false;
        if (!membership.includes(toMembershipStatus(g.membershipStatus))) {
          return false;
        }
      }
      if (domains.length > 0) {
        const mapping = this.groupDomainMap.get(g.id.toLowerCase());
        const allDomains = mapping?.allDomains || [];
        if (!allDomains.some((dc) => domains.includes(dc))) return false;
      }
      return true;
    });
  }

  /** How many companies the current query shows, for the sentence above the list. */
  private get shownCount(): number {
    return this.filterGroups(this.filterSelection, this.search).length;
  }

  /** Check if filtered results would be empty (for showing no-matches state) */
  private get hasNoMatches(): boolean {
    if (this.groups.length === 0) return false;
    return this.shownCount === 0;
  }

  /**
   * The facets the rail renders, with a count beside every value.
   *
   * Each count is taken with its OWN facet's ticks ignored - see `filterGroups`. Options are
   * declared in a fixed order rather than derived from the data, so the rail does not reshuffle
   * itself as companies are added or filtered.
   */
  private get filterFacets(): FilterFacet[] {
    const countBy = (
      facetKey: string,
      predicate: (g: LoginGroup) => boolean
    ) => {
      const without: FilterSelection = { ...this.filterSelection };
      without[facetKey] = [];
      return this.filterGroups(without, this.search).filter(predicate).length;
    };

    const domainsOf = (g: LoginGroup): string[] =>
      this.groupDomainMap.get(g.id!.toLowerCase())?.allDomains ?? [];

    return [
      {
        key: FACET_OPERATING,
        title: 'Operating state',
        phrase: 'that are',
        options: [
          { value: OPERATING_ACTIVE, label: operatingStatusLabel(true) },
          { value: OPERATING_BLOCKED, label: operatingStatusLabel(false) },
        ].map((o) => ({
          ...o,
          count: countBy(
            FACET_OPERATING,
            (g) => (g.active ? OPERATING_ACTIVE : OPERATING_BLOCKED) === o.value
          ),
        })),
      },
      {
        key: FACET_MEMBERSHIP,
        // The column beside this rail already read the glossary, so the same screen was spelling
        // the concept two ways - one that a rename would reach and one it would not.
        title: `${terms.membership.value}`,
        phrase: `whose ${terms.membership.lower} is`,
        options: MEMBERSHIP_ORDER.map((status) => ({
          value: status,
          label: membershipLabel(status),
          count: countBy(
            FACET_MEMBERSHIP,
            (g) =>
              g.membershipStatus !== undefined &&
              toMembershipStatus(g.membershipStatus) === status
          ),
        })),
      },
      {
        key: FACET_TYPES,
        title: terms.userGroupDomain.plural,
        phrase: 'operating as',
        // Labelled through the SAME function the row chips use, so the filter and the chips cannot
        // disagree. Taking the catalog's own `displayName` is what put "Product Registry" in the
        // rail beside rows chipped "Prime System Manufacturer" - two names for one type on one
        // screen.
        options: this.availableDomains.map((d) => ({
          value: d.code!,
          label: getOrganizationTypeLabel(d.code),
          count: countBy(FACET_TYPES, (g) =>
            domainsOf(g).some((code) => isSame(code, d.code ?? ''))
          ),
        })),
      },
    ];
  }

  /** Whether anything at all is narrowing the list, the search box included. */
  private get anyFilterApplied(): boolean {
    return this.search.trim() !== '' || !isSelectionEmpty(this.filterSelection);
  }

  /** Sites of a company whose name contains the given lowercased text. */
  private sitesMatchingText(
    companyId: string,
    text: string
  ): GroupSiteSummary[] {
    const sites = this.groupSitesMap.get(companyId.toLowerCase()) || [];
    return sites.filter((site) => site.name?.toLowerCase().includes(text));
  }

  /**
   * Keep every company's sites open, always.
   *
   * Architect's ruling, 2026-08-10: a site is not detail a person opts into - it is part of what the
   * company IS on this screen, and collapsing it hid the thing the list exists to show. The expander
   * column went with it, because a control whose state never changes is noise.
   *
   * Stated as a whole set on every fetch rather than toggled, so companies arriving from a later
   * page or a re-filter are open too - a set computed once at load would leave them shut.
   *
   * A company whose sites were all narrowed away by `siteSurvivesNarrowing` is left out, because
   * marking a row expanded when it has nothing beneath it is a claim the data does not support.
   *
   * Deferred to a microtask because this runs INSIDE the data provider, during the table's own
   * fetch. Setting table state synchronously there would mutate a component mid-update, which Lit
   * warns about and which can drop the change. `setExpandedChildRows` no-ops when the set is
   * unchanged, so the deferred call cannot loop.
   */
  private applyAutoExpansion(rows: GroupRow[]) {
    const withSites = rows.filter(
      (row) => this.siteRowsFor(row as object).length > 0
    );
    void Promise.resolve().then(() =>
      this.dataTable?.setExpandedChildRows(withSites)
    );
  }

  getImisReference(group: LoginGroup): string {
    const imis = this.imis.find((i) => i.loginGroupId === group.id);
    return imis ? imis.reference : '';
  }

  render() {
    return html`
      <div class="view-groups fill-height-page">
        <!--
          The search row, the legend and the list are ONE block that can take the whole viewport
          in place (D-106): searching while scanning the rows is the common case, and the legend
          explains the labels in the rows it travels with. Both sit in the header slot, which is
          what leaves the expander on the sentence line rather than beside the legend.
        -->
        <agwa-fullscreen-block .title=${terms.loginGroup.plural}>
          <div
            class="group-filter-bar layout-row-wrap fill-height-header"
            slot="header"
          >
            <div class="filters-left layout-row-gap">
              <!--
              Sized small at this call site rather than by changing the component's default. Its
              "large pill" default is the modules-hub aesthetic and is right there; on a dense list
              screen it rendered a 20px placeholder beside 12px filter labels and a spreadsheet-tight
              table, which is the single largest thing on a page whose subject is the table.
            -->
              <agwa-search
                size="small"
                .value=${this.search}
                placeholder="Search ${terms.loginGroup.lowerPlural} and ${terms.loginSite.lowerPlural}..."
                @search-input=${this.handleSearchInput}
                @search-change=${this.handleSearchInput}
                @search-clear=${this.handleSearchClear}
              ></agwa-search>
            </div>
            <div class="actions-right">
              <sl-button variant="primary" @click=${this.goToAddGroup}>
                <sl-icon slot="prefix" name="plus"></sl-icon>
                Add ${terms.loginGroup.value}
              </sl-button>
            </div>
          </div>
          <!--
          Between the filters and the table, and outside every loading / error / empty branch below:
          the labels need explaining whether or not there is a row carrying one, and a legend that
          disappears when the list is empty is missing exactly when a new reader is most likely to
          be looking at this screen.
        -->
          <member-legend
            slot="header"
            .operationTypes=${this.domainCatalogue
              .filter((d) => d.shortCode)
              .map((d) => ({
                shortCode: d.shortCode as string,
                name: getOrganizationTypeLabel(d.code),
              }))}
          ></member-legend>
          <!--
          The rail is a SIBLING of the list, not a strip above it. Every value it offers is legible
          without opening anything, and ticking one changes a checkbox that was already on screen -
          so unlike the dropdowns this replaced, the filters occupy the same space before and after
          a choice and the rows beneath never move.
        -->
          <div class="list-body">
            <agwa-filter-rail
              .facets=${this.filterFacets}
              .selection=${this.filterSelection}
              ?can-clear=${this.anyFilterApplied}
              @filter-change=${this.handleFilterChange}
              @filter-clear=${this.handleFilterClear}
            ></agwa-filter-rail>
            <div class="list-main">
              <!--
              What the ticks MEAN together, in words. The rail can show which boxes are set; only
              this can show that values within a facet are OR'd while facets are AND'ed, which is
              the thing someone needs when a company they expected is not in the list.
            -->
              <agwa-filter-summary
                .facets=${this.filterFacets}
                .selection=${this.filterSelection}
                search-text=${this.search}
                subject=${terms.loginGroup.lowerPlural}
                shown-count=${this.shownCount}
                total-count=${this.groups.length}
                @filter-drop=${this.handleFilterDrop}
              ></agwa-filter-summary>
              ${
                this.loading
                  ? html`<agwa-empty-state
                      icon="hourglass-split"
                      title="Loading"
                      message="Fetching ${terms.loginGroup.lowerPlural}..."
                    ></agwa-empty-state>`
                  : this.error
                    ? html`<agwa-empty-state
                        icon="exclamation-triangle"
                        title="Error"
                        .message=${this.error}
                      ></agwa-empty-state>`
                    : this.groups.length === 0 && !this.search
                      ? html`<agwa-empty-state
                          icon="layers"
                          title="No ${terms.loginGroup.lowerPlural} yet"
                          message="Add your first ${terms.loginGroup.lower} to get started."
                        >
                          <sl-button
                            variant="primary"
                            size="small"
                            @click=${this.goToAddGroup}
                          >
                            <sl-icon slot="prefix" name="plus"></sl-icon>Add
                            ${terms.loginGroup.value}
                          </sl-button>
                        </agwa-empty-state>`
                      : html`
                          ${
                            this.hasNoMatches
                              ? html`<agwa-empty-state
                                  icon="search"
                                  title="No matches"
                                  message="No ${terms.loginGroup.lowerPlural} match every condition above. Remove one, or clear them all."
                                >
                                  <!--
                              Clears the ticks AND the search, matching the rail's own control.
                              Offering only "clear search" here used to hand back a list still
                              narrowed by filters the person had just asked to be rid of.
                            -->
                                  <sl-button
                                    size="small"
                                    @click=${this.handleFilterClear}
                                    >Clear all filters</sl-button
                                  >
                                </agwa-empty-state>`
                              : ''
                          }
                          <agwa-lit-table
                            fill-height
                            .columns=${this.groupColumns}
                            .dataProvider=${this.groupsProvider}
                            .pageLength=${1000}
                            .autoFetchOnLoad=${true}
                            .keyevent=${(item: GroupRow) => item.id}
                            .childRowsProvider=${this.siteRowsFor}
                            tablestyle="standardtable"
                            style=${this.hasNoMatches ? 'display: none' : ''}
                            .clickrows=${true}
                            @rowclick=${(e: CustomEvent<{ item: GroupRow }>) =>
                              this.openRow(e.detail.item)}
                            @navigate=${(e: CustomEvent<{ href: string }>) =>
                              router?.navigate(e.detail.href)}
                          ></agwa-lit-table>
                        `
              }
            </div>
          </div>
        </agwa-fullscreen-block>
        ${
          /* Card grid preserved but hidden - uncomment to test on small screens
          <div class="card-grid" role="list">
            ${this.groups.map(
              (g) =>
                html`<div class="user-card" role="listitem">
                  <header>
                    <div class="name-block">
                      <span class="display-name">${g.name}</span>
                    </div>
                    <sl-tag
                      size="small"
                      variant="${g.active ? 'success' : 'danger'}"
                      style="margin-left:auto"
                      >${g.active ? 'Active' : 'Inactive'}</sl-tag
                    >
                  </header>
                  <div class="user-meta">
                    ${this.getImisReference(g)
                      ? html`<span
                          ><strong>iMIS:</strong>
                          ${this.getImisReference(g)}</span
                        >`
                      : ''}
                  </div>
                  <div class="card-actions">
                    <sl-button
                      size="small"
                      variant="default"
                      @click=${() => this.navigateToMember(g.id!)}
                    >
                      Overview
                    </sl-button>
                    <sl-button
                      size="small"
                      variant="primary"
                      @click=${() => this.navigateToMemberStaff(g.id!)}
                    >
                      Staff
                    </sl-button>
                  </div>
                </div>`
            )}
          </div>
          */ ''
        }
      </div>
    `;
  }

  /**
   * Adding a company happens on its own routed page, not a dialog (D-089): the form is too
   * large for a dialog and the list needs no refresh wiring - returning to this route
   * refetches on its own.
   */
  private goToAddGroup() {
    void router?.navigate(resolveRouterPath('maintenance/companies/new'));
  }
}
